/**
 Globo Product Options Integration for Discount Savings Block
 Dynamically recalculates discount savings when customers add/remove product options
 via Globo Product Options app (e.g., adding mixer tap to Hip Duo filter system)
 
Developer Notes:
   - Author: Alec Hewstone (Kubix Media)
   - Date: 11/11/2025

Context & Communication with Globo Support:
   Initial Challenge:
     - Discount savings block displayed "Save £74.99" on Hip Duo base product (£299.95)
     - When customers added £199 mixer tap via Globo, price updated to £499.90
     - But discount block still showed £74.99 instead of £124.98
     - Globo updates price client-side without triggering Shopify variant events
   
   Solution from Globo Support (Helen):
     - Globo emits custom event: 'gpoElementChanged' when options change
     - Event fires whenever customer adds/removes product options
     - Suggested implementation: document.addEventListener("gpoElementChanged", ...)

Architecture Overview:
   How It Works:
     1. Listens for Globo's 'gpoElementChanged' event
     2. Waits 250ms for Globo's DOM updates to complete (tested extensively)
     3. Reads updated total price from .f-price-item--regular element
     4. Gets discount percentage from product metafield (via data attributes)
     5. Recalculates savings and updates discount block display
   
   Key Technical Details:
     - Uses setTimeout(250ms) - tested extensively to balance speed vs reliability
     - Reads from .f-price-item--regular NOT .f-price-item--sale 
       (Globo puts total in "regular" price element, not "sale")
     - Respects manual override metafields (skips calculation if override active)
     - Scoped by section ID to handle multiple products on same page

Important Constraints:
   CRITICAL: All add-ons must have matching discount percentages
   
   Why: This script applies the parent product's discount % to the total price.
   
   Example (WORKS):
     - Hip Duo: 25% discount
     - Mixer Tap: 25% discount
     - Total £499.90 × 25% = £124.98 savings
   
   Example (FAILS):
     - Hip Duo: 25% discount (£299.95 × 25% = £74.99)
     - Mixer Tap: 15% discount (£199 × 15% = £29.85)
     - Real savings: £104.84
     - But script shows: £124.98 (£499.90 × 25%)
   
   Solution for Mixed Discounts:
     Use manual override metafield: custom.discount_savings_text_override
     Set to: "Save up to £XXX.XX when adding mixer tap (or other add-on)"

Future Considerations:
   If Globo integration becomes more complex:
     - Consider creating shared Globo event utility
     - Abstract price reading logic if other blocks need it
     - Add configuration for different delay timings per product
   
   For now: Kept simple and specific to discount savings block

Testing Notes:
   Tested Scenarios:
     Hip Duo: Add/remove mixer tap → savings updates correctly
     Hip Trio: Same behavior as Hip Duo
     Standard products (no Globo): No interference, works normally
     Manual override products: Skips calculation, shows custom text
     Multiple discount blocks on page: Each updates independently

@see snippets/discount-savings.liquid for Liquid implementation
@see sections/main-product.liquid for block schema
 */

(() => {
    if (window.__DiscountSavingsGloboInitialized) return;
    window.__DiscountSavingsGloboInitialized = true;
  
    const parsePriceToCents = (priceText) => {
      if (!priceText) return null;
      const cleaned = priceText.replace(/[\s]/g, '').replace(/[^0-9.,-]/g, '');
      if (!cleaned) return null;
  
      let normalised = cleaned;
      const hasComma = cleaned.includes(',');
      const hasDot = cleaned.includes('.');
  
      if (hasComma && hasDot) {
        normalised = cleaned.lastIndexOf('.') > cleaned.lastIndexOf(',')
          ? cleaned.replace(/,/g, '')
          : cleaned.replace(/\./g, '').replace(',', '.');
      } else if (hasComma) {
        normalised = cleaned.replace(/\./g, '').replace(',', '.');
      }
  
      const value = Number(normalised);
      if (Number.isNaN(value)) return null;
      return Math.round(value * 100);
    };
  
    const formatMoney = (amount) => {
      if (window.FoxTheme?.Currency?.formatMoney) {
        return FoxTheme.Currency.formatMoney(amount, FoxTheme.settings?.moneyFormat || '{{amount}}');
      }
      return (amount / 100).toFixed(2);
    };
  
    const updateAmountText = (amountSpan, baseText, percentageSpan, percentageValue, template, showPercentage) => {
      if (!amountSpan) return;
  
      if (percentageSpan) {
        const textNode = Array.from(amountSpan.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);
        if (textNode) {
          textNode.textContent = baseText;
        } else {
          amountSpan.insertBefore(document.createTextNode(baseText), percentageSpan);
        }
  
        if (showPercentage) {
          if (template) {
            percentageSpan.textContent = template.replace('[[value]]', percentageValue);
          } else {
            percentageSpan.textContent = ` (${percentageValue}% off)`;
          }
        } else {
          percentageSpan.remove();
        }
      } else {
        amountSpan.textContent = baseText;
      }
    };
  
    const updateBlock = (block, priceInCents) => {
      if (!block || block.dataset.useOverride === 'true') return;
  
      const infoWrapper = block.querySelector('[data-discount-percentage]');
      if (!infoWrapper) return;
  
      const percentageAttr = infoWrapper.getAttribute('data-discount-percentage');
      const discountPercentage = Number(percentageAttr);
      if (!percentageAttr || Number.isNaN(discountPercentage)) return;
  
      const savingsCents = Math.round((priceInCents * discountPercentage) / 100);
      const formattedSavings = formatMoney(savingsCents);
  
      infoWrapper.setAttribute('data-savings-amount', savingsCents.toString());
  
      const amountSpan = block.querySelector('.product__discount-savings__amount');
      if (!amountSpan) return;
  
      const prefixText = (block.dataset.prefixText || '').trim();
      const baseText = prefixText ? `${prefixText} ${formattedSavings}` : formattedSavings;
  
      const showPercentage = block.dataset.showPercentage === 'true';
      const percentageSpan = amountSpan.querySelector('.product__discount-savings__percentage');
      const percentageTemplate = percentageSpan?.dataset.percentageTemplate;
  
      updateAmountText(amountSpan, baseText, percentageSpan, discountPercentage, percentageTemplate, showPercentage);
    };
  
    const handleUpdate = () => {
      window.setTimeout(() => {
        const blocks = document.querySelectorAll('[data-discount-block]');
        if (!blocks.length) return;
  
        blocks.forEach((block) => {
          const sectionId = block.dataset.section;
          if (!sectionId) return;
  
          const priceSelector = `#price-${sectionId} .f-price-item--regular`;
          const priceElement = document.querySelector(priceSelector);
  
          if (!priceElement) {
            console.warn('[Discount Savings] Globo integration: price element not found for section', sectionId);
            return;
          }
  
          const priceText = priceElement.textContent?.trim();
          const priceInCents = parsePriceToCents(priceText);
  
          if (priceInCents == null) {
            console.warn('[Discount Savings] Globo integration: unable to parse price text', priceText);
            return;
          }
  
          updateBlock(block, priceInCents);
        });
      }, 250); // 250ms delay to ensure price is updated via Globo - tested extensively
    };
  
    document.addEventListener('gpoElementChanged', handleUpdate);
  })();